import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "../config/axios";
import PageWrapper from "../components/common/PageWrapper";
import Header from "../components/common/Header";
import BodyWrapper from "../components/common/BodyWrapper";
import Footer from "../components/common/Footer";
import ScrollToTop from "../components/common/ScrollToTop";
import ExpandedModal from "../components/common/mediaGallery/ExpandedModal";
import { useGeneral } from "../components/contexts/GeneralContext";
import { useParams } from "react-router-dom";
import FullScreenLoading from "../components/common/loading/FullScreenLoading";
import ApiErrorMessage from "../components/common/ApiErrorMessage";
import NoItemsFound from "../components/common/NoItemsFound";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination, Autoplay } from "swiper/modules";
import { cleanHTML, cleanHTML2, isSnack } from "../components/utils/commonHelper";
import { FaBowlRice } from "react-icons/fa6";
import SelectBox from "../components/common/SelectBox";
import GeneralButton from "../components/common/GeneralButton";
const usePageContent = (filter) => {
  const [loading, setLoading] = useState(true);
  const [content, setContent] = useState(null);
  const [error, setError] = useState("");
  const { discountCoupons } = useGeneral();
  const { meal } = useParams();

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `get-meal-menu/${meal}?${filter ? `vegOrNonVeg=${filter}` : ""}`
        );
        if (active) setContent(data);
      } catch (err) {
        console.error(err);
        if (active) setError("Failed to load plans. Please try again later.");
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [filter]);
  return { content, loading, error, discountCoupons, meal };
};

const useScrollToTop = () => {
  useEffect(() => {
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
    return () => clearTimeout(timer);
  }, []);
};

const MealsMenuPage = () => {
  const [selectValue, setSelectValue] = useState(""); // filter value
  const [selectedSubId, setSelectedSubId] = useState(null);
  const [activeIndexMap, setActiveIndexMap] = useState({}); // ← ⭐ store active index per meal
  const { content, loading, error, discountCoupons } =
    usePageContent(selectValue);
  useScrollToTop();
  const handleShowModal = (mealItem) => {
    setSelectedSubId(mealItem);
  };
  const handleCloseModal = () => setSelectedSubId(null);
  const handleConfirm = () => setSelectedSubId(null);
  const handleSelectChange = (val) => {
    setSelectValue(val);
  };

  return (
    <>
      {loading && <FullScreenLoading />}

      <PageWrapper loading={loading} className="bg-bg min-h-screen">
        <Header code={discountCoupons} />

        <BodyWrapper loading={loading}>
          {!loading && error ? (
            <ApiErrorMessage
              message1="Failed to load menu"
              message2="Please check your connection and try again."
            />
          ) : !loading &&
            (!content?.data?.menu || content?.data?.menu.length <= 0) ? (
            <NoItemsFound
              message1="No menu available"
              message2="Please check back later."
            />
          ) : (
            <div
              className={`bg-[#DEE2DD]/40 rounded-[15px] p-6 md:px-12 lg:px-14 1xl:px-20 xl:py-12 relative`}
            >
              <h1 className="text-36pt mb-2 capitalize">
                {content?.data?.title.toLowerCase() === "lunch"
                  ? "Lunch Box"
                  : ` ${
                      isSnack(content?.data?.title)
                        ? "Snacks"
                        : content?.data?.title
                    } Menu`}
              </h1>

              {/* -------- Meal Header Section -------- */}
              <div className="flex  flex-col md:flex-row md:items-center  lg:items-start gap-6 md:gap-4 xl:gap-6 mb-0">
                <div className="flex-1 min-w-0 ">
                  <div
                    className="terms-container mb-2"
                    dangerouslySetInnerHTML={{
                      __html: cleanHTML2(content?.data?.description),
                    }}
                  />

                  {content?.data?.price && (
                    <div className="flex items-center flex-wrap text-20pt mb-2 md:hidden">
                      {content?.data?.price?.$numberDecimal && (
                        <span className="border border-green bg-green/10 text-green px-3 py-1 rounded-full">
                          Price : {content?.data?.price?.$numberDecimal} AED /
                          Meal
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div
                  title="Click to enlarge image"
                  onClick={() => handleShowModal(content?.data)}
                  className="flex-shrink-0 w-full mb-6 md:mb-0 relative w-[220px] h-[220px]  max-w-[250px] md:w-[240px] md:h-[240px] rounded-xl overflow-hidden cursor-pointer xl:mx-6"
                >
                  <img
                    src={require("../assets/images/plate.png")}
                    alt={content?.data?.title}
                    className=" w-[220px] h-[220px] md:w-full md:h-full object-contain absolute"
                    onError={(e) => {
                      e.currentTarget.src = require("../assets/images/link.png");
                      e.currentTarget.classList.add("object-contain");
                    }}
                  />
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}public/${content?.data?.image}`}
                    alt={content?.data?.title}
                    className="w-full h-full object-contain absolute left-7 md:left-7 md:top-4 z-[10] w-[150px] h-[150px] max-w-[180px] md:max-w-[200px] md:w-[200px] md:h-[200px]"
                    onError={(e) => {
                      e.currentTarget.src = require("../assets/images/link.png");
                      e.currentTarget.classList.add("object-contain");
                    }}
                  />
                </div>
              </div>
              <div className="flex items-center justify-end md:justify-between flex-wrap">
                {content?.data?.price && (
                  <div className="md:flex items-center flex-wrap text-20pt mb-2 hidden">
                    {content?.data?.price?.$numberDecimal && (
                      <span className="border border-green bg-green/10 text-green px-3 py-1 rounded-full">
                        Price : {content?.data?.price?.$numberDecimal} AED /
                        Meal
                      </span>
                    )}
                  </div>
                )}

                {content?.data?.menu?.length > 0 && (
                  <div className="mb-2 flex flex-col  items-start md:items-center justify-between gap-2 md:mt-2 mt-0 min-w-[250px]">
                    <div className="flex-1 flex items-center justify-end gap-2 w-full">
                      <SelectBox
                        value={selectValue}
                        setValue={setSelectValue}
                        options={[
                          { label: "Veg", value: "Veg" },
                          { label: "Non Veg", value: "Non Veg" },
                        ]}
                        placeholder="Choose a Type"
                        onSelect={handleSelectChange}
                      />
                    </div>
                  </div>
                )}
              </div>

              {selectValue && (
                <div className="flex items-center flex-wrap gap-2 mb-2 px-3 py-2 bg-[#D06F36]/10 rounded-lg w-full">
                  {selectValue && (
                    <p className="text-sm text-gray-700 flex items-center gap-1">
                      Filter Applied:{" "}
                      <span className="text-[#D06F36]">{selectValue}</span>
                      <button
                        onClick={() => handleSelectChange("")}
                        className="ml-1 text-xs text-[#D06F36] font-medium hover:underline transition-all duration-200"
                      >
                        ×
                      </button>
                    </p>
                  )}

                  {selectValue && (
                    <button
                      onClick={() => {
                        handleSelectChange("");
                      }}
                      className="ml-auto text-xs text-[#D06F36] font-medium hover:underline transition-all duration-200"
                    >
                      Clear All
                    </button>
                  )}
                </div>
              )}

              {/* -------- Menu Items -------- */}
              <div
                className={
                  content?.data?.menu?.length === 1
                    ? "grid grid-cols-1 mt-2"
                    : "grid grid-cols-1 1xl:grid-cols-2 gap-2 md:gap-4 lg:gap-8 mt-2"
                }
              >
                {content?.data?.title.toLowerCase() === "lunch" &&
                  content?.data?.menu
                    ?.sort((a, b) => a.sortOrder - b.sortOrder)
                    .filter((a, index) => a.status === "1")
                    .map((mealItem, index) => (
                      <div
                        key={mealItem._id}
                        className="group relative bg-white rounded-xl p-6 pb-12 md:px-8 lg:px-8 lg:py-6 gap-4"
                      >
                        {/* Left info */}

                        <div className="flex flex-col md:flex-row  lg:items-center justify-between gap-6">
                          <div className="flex-1 w-full">
                            <p className="text-28pt mb-4">{mealItem.title}</p>
                            <div className="flex items-center flex-wrap gap-3 mb-3">
                              {mealItem.vegOrNonVeg && (
                                <span
                                  className={`flex items-center gap-1 text-14pt px-3 py-1 rounded-full ${
                                    mealItem.vegOrNonVeg === "Veg"
                                      ? "bg-green/10 text-green"
                                      : "bg-red-100 text-red-700"
                                  }`}
                                >
                                  <span
                                    className={`w-2.5 h-2.5 me-1 rounded-full ${
                                      mealItem.vegOrNonVeg === "Veg"
                                        ? "bg-green/80"
                                        : "bg-red-600"
                                    }`}
                                  ></span>
                                  {mealItem.vegOrNonVeg}
                                </span>
                              )}

                              {mealItem.hotOrCold && (
                                <span
                                  className={`text-14pt px-3 py-1 rounded-full ${
                                    mealItem.hotOrCold === "Hot"
                                      ? "bg-orange/20 text-orange"
                                      : "bg-blue-100 text-blue-500"
                                  }`}
                                >
                                  {mealItem.hotOrCold}
                                </span>
                              )}

                              {mealItem.nutritionalInfo && (
                                <div className="flex flex-wrap gap-2">
                                  {mealItem.nutritionalInfo
                                    .split(",")
                                    .map((nutri, idx) => (
                                      <span
                                        key={idx}
                                        className="border border-quaternary text-quaternary text-14pt px-2 py-1 rounded-full"
                                      >
                                        {nutri.trim()}
                                      </span>
                                    ))}
                                </div>
                              )}
                            </div>

                            {/* Allergens */}
                            {mealItem.allergens?.filter((a) => a.trim() !== "")
                              .length > 0 && (
                              <div className="w-fit bg-orange/10 border-l-4 border-orange p-2 mt-4 rounded-md shadow-sm flex items-center gap-2">
                                <motion.span
                                  animate={{
                                    rotate: [0, -10, 10, -10, 0],
                                    scale: [1, 1.15, 1.05, 1.15, 1],
                                  }}
                                  transition={{
                                    duration: 1.4,
                                    repeat: Infinity,
                                    ease: "easeInOut",
                                  }}
                                  className="text-orange text-xl"
                                >
                                  ⚠️
                                </motion.span>

                                <p className="text-orange text-18pt font-sans">
                                  Allergens:{" "}
                                  {mealItem.allergens
                                    .filter((a) => a.trim() !== "")
                                    .join(", ")}
                                </p>
                              </div>
                            )}

                            {/* Description */}
                          </div>

                          {/* ------- Image Swiper ------- */}
                          <div
                            title="Click to enlarge image"
                            className="flex-shrink-0 w-[200px] h-[200px] cursor-pointer"
                            onClick={() => handleShowModal(mealItem)}
                          >
                            <Swiper
                              modules={[Pagination, Autoplay]}
                              pagination={{ clickable: true }}
                              autoHeight={true}
                              className="w-full h-full review small"
                              onSlideChange={(s) => {
                                setActiveIndexMap((prev) => ({
                                  ...prev,
                                  [mealItem._id]: s.realIndex,
                                }));
                              }}
                            >
                              {/* Main Image */}
                              {mealItem?.image && (
                                <SwiperSlide
                                  key="main"
                                  className={`cursor-pointer flex items-center justify-center bg-white aspect-[1/1] relative  hover:shadow-lg transition-shadow ${
                                    mealItem.pictures.length > 0 ? "pb-10" : ""
                                  }`}
                                >
                                  <img
                                    src={`${process.env.REACT_APP_BACKEND_URL}public/${mealItem.image}`}
                                    alt={mealItem.title}
                                    onError={(e) => {
                                      e.currentTarget.src = require("../assets/images/link.png");
                                      e.currentTarget.classList.add(
                                        "object-contain"
                                      );
                                    }}
                                    className="w-full h-[200px] object-contain bg-gray rounded-lg"
                                  />
                                </SwiperSlide>
                              )}

                              {/* Extra Images */}
                              {Array.isArray(mealItem?.pictures) &&
                                mealItem.pictures.slice(0, 2).map((img, i) => (
                                  <SwiperSlide
                                    key={i}
                                    className={`cursor-pointer flex items-center justify-center bg-white aspect-[1/1] relative ${
                                      mealItem.pictures.length > 0
                                        ? "pb-10"
                                        : ""
                                    }`}
                                  >
                                    <img
                                      src={`${process.env.REACT_APP_BACKEND_URL}public/${img.image}`}
                                      alt={mealItem.title}
                                      onError={(e) => {
                                        e.currentTarget.src = require("../assets/images/link.png");
                                        e.currentTarget.classList.add(
                                          "object-contain"
                                        );
                                      }}
                                      className="w-full h-[200px] object-contain bg-gray rounded-lg"
                                    />
                                  </SwiperSlide>
                                ))}
                            </Swiper>
                          </div>
                        </div>

                        <div
                          className={`text-sm text-gray-700 leading-relaxed ${
                            mealItem.pictures.length > 0 ? "mt-10 " : "mt-4  "
                          }`}
                        >
                          {mealItem.description && (
                            <p
                              className={`subtitle text-24pt  font-mono ${"abouthtml1"}`}
                              dangerouslySetInnerHTML={{
                                __html: cleanHTML(mealItem.description),
                              }}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                {content?.data?.title.toLowerCase() != "lunch" &&
                  content?.data?.menu
                    ?.sort((a, b) => a.sortOrder - b.sortOrder)
                    ?.filter((a, index) => a.status === "1")
                    .map((mealItem, index) => (
                      <div
                        key={mealItem._id}
                        className="group relative bg-white rounded-xl p-6 pb-12 md:px-8 lg:px-8 lg:py-6 flex flex-col md:flex-row items-center gap-4"
                      >
                        {/* Left info */}
                        <div className="flex-1 w-full">
                          <p className="text-34pt leading-none mb-4">
                            {mealItem.title}
                          </p>

                          <div className="flex items-center flex-wrap gap-3 mb-3">
                            {mealItem.vegOrNonVeg && (
                              <span
                                className={`flex items-center gap-1 text-14pt px-3 py-1 rounded-full ${
                                  mealItem.vegOrNonVeg === "Veg"
                                    ? "bg-green/10 text-green"
                                    : "bg-red-100 text-red-700"
                                }`}
                              >
                                <span
                                  className={`w-2.5 h-2.5 me-1 rounded-full ${
                                    mealItem.vegOrNonVeg === "Veg"
                                      ? "bg-green/80"
                                      : "bg-red-600"
                                  }`}
                                ></span>
                                {mealItem.vegOrNonVeg}
                              </span>
                            )}

                            {mealItem.hotOrCold && (
                              <span
                                className={`text-14pt px-3 py-1 rounded-full ${
                                  mealItem.hotOrCold === "Hot"
                                    ? "bg-orange/20 text-orange"
                                    : "bg-blue-100 text-blue-500"
                                }`}
                              >
                                {mealItem.hotOrCold}
                              </span>
                            )}

                            {mealItem.nutritionalInfo && (
                              <div className="flex flex-wrap gap-2">
                                {mealItem.nutritionalInfo
                                  .split(",")
                                  .map((nutri, idx) => (
                                    <span
                                      key={idx}
                                      className="border border-quaternary text-quaternary text-14pt px-2 py-1 rounded-full"
                                    >
                                      {nutri.trim()}
                                    </span>
                                  ))}
                              </div>
                            )}
                          </div>

                          {/* Allergens */}
                          {mealItem.allergens?.filter((a) => a.trim() !== "")
                            .length > 0 && (
                            <div className="w-fit bg-orange/10 border-l-4 border-orange p-2 mt-4 rounded-md shadow-sm flex items-center gap-2">
                              <motion.span
                                animate={{
                                  rotate: [0, -10, 10, -10, 0],
                                  scale: [1, 1.15, 1.05, 1.15, 1],
                                }}
                                transition={{
                                  duration: 1.4,
                                  repeat: Infinity,
                                  ease: "easeInOut",
                                }}
                                className="text-orange text-xl"
                              >
                                ⚠️
                              </motion.span>

                              <p className="text-orange text-18pt font-sans">
                                Allergens:{" "}
                                {mealItem.allergens
                                  .filter((a) => a.trim() !== "")
                                  .join(", ")}
                              </p>
                            </div>
                          )}

                          {/* Description */}
                          <div className="text-sm text-gray-700 mt-4 leading-relaxed max-w-xl ">
                            {mealItem.ingredients && (
                              <p className="text-24pt font-mono flex items-center flex-wrap gap-0 ">
                                <FaBowlRice
                                  size={24}
                                  className="text-quaternary me-2"
                                />
                                <span className="font-sans  leading-relaxed">
                                  Ingredients:
                                </span>
                                <span className="leading-[35px] text-[22px] capitalize">
                                  {" "}
                                  {mealItem.ingredients}
                                </span>
                              </p>
                            )}
                            {mealItem.description && (
                              <p
                                className="subtitle text-24pt abouthtml1 font-mono "
                                dangerouslySetInnerHTML={{
                                  __html: cleanHTML(mealItem.description),
                                }}
                              />
                            )}
                          </div>
                        </div>

                        {/* ------- Image Swiper ------- */}
                        <div
                          title="Click to enlarge image"
                          className="flex-shrink-0 w-[200px] h-[200px] cursor-pointer"
                          onClick={() => handleShowModal(mealItem)}
                        >
                          <Swiper
                            modules={[Pagination, Autoplay]}
                            pagination={{ clickable: true }}
                            autoHeight={true}
                            className="w-full h-full review small"
                            onSlideChange={(s) => {
                              setActiveIndexMap((prev) => ({
                                ...prev,
                                [mealItem._id]: s.realIndex,
                              }));
                            }}
                          >
                            {/* Main Image */}
                            {mealItem?.image && (
                              <SwiperSlide
                                key="main"
                                className="cursor-pointer flex items-center justify-center bg-white aspect-[1/1] relative pb-10 hover:shadow-lg transition-shadow"
                              >
                                <img
                                  src={`${process.env.REACT_APP_BACKEND_URL}public/${mealItem.image}`}
                                  alt={mealItem.title}
                                  onError={(e) => {
                                    e.currentTarget.src = require("../assets/images/link.png");
                                    e.currentTarget.classList.add(
                                      "object-contain"
                                    );
                                  }}
                                  className="w-full h-[200px] object-contain bg-gray rounded-lg"
                                />
                              </SwiperSlide>
                            )}

                            {/* Extra Images */}
                            {Array.isArray(mealItem?.pictures) &&
                              mealItem.pictures.slice(0, 2).map((img, i) => (
                                <SwiperSlide
                                  key={i}
                                  className="cursor-pointer flex items-center justify-center bg-white aspect-[1/1] relative pb-10"
                                >
                                  <img
                                    src={`${process.env.REACT_APP_BACKEND_URL}public/${img.image}`}
                                    alt={mealItem.title}
                                    onError={(e) => {
                                      e.currentTarget.src = require("../assets/images/link.png");
                                      e.currentTarget.classList.add(
                                        "object-contain"
                                      );
                                    }}
                                    className="w-full h-[200px] object-contain bg-gray rounded-lg"
                                  />
                                </SwiperSlide>
                              ))}
                          </Swiper>
                        </div>
                      </div>
                    ))}

                {/* EXPANDED MODAL */}
                {selectedSubId && (
                  <ExpandedModal
                    isOpen={true}
                    onClose={handleCloseModal}
                    onConfirm={handleConfirm}
                    data={{
                      title: selectedSubId?.title,
                      pictures: [
                        { image: selectedSubId?.image },
                        ...(selectedSubId?.pictures || []),
                      ],
                      videos: [],
                    }}
                    initialIndex={activeIndexMap[selectedSubId?._id] || 0}
                  />
                )}
              </div>
            </div>
          )}
        </BodyWrapper>

        <Footer />
      </PageWrapper>
    </>
  );
};

export default MealsMenuPage;
